// Live relay state for a switch-type device, shared by both UIs.
//
// Two things matter here, and both are lifted straight from the Flutter screens:
//
// 1. UNKNOWN IS NOT OFF. Until a real status arrives (server seed or MQTT), each
//    relay is "unknown" and the UI paints it BLUE. Flutter learned this the hard
//    way: defaulting to OFF made a running load look switched off whenever the
//    seed fetch was slow.
// 2. The UI never guesses. Tapping a relay publishes a command and nothing else;
//    the colour changes only when the DEVICE echoes its status back. So what you
//    see is always what the hardware reported.
//
// Status wire format on `<uno>/client` is `[<uno>]<status>#<userId>`, and for
// switch devices <status> is a CSV of 0/1 — one per relay.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  subscribeTopic, unsubscribeTopic, addMessageListener,
} from '../mqtt/mqttClient';
import { fetchSeedStatus } from '../devices/deviceStatus';
import { publishCommand } from '../utils/deviceController';

// Parse a CSV status into { states, known } for `count` relays.
function parseCsv(raw, count) {
  const parts = String(raw ?? '').trim().split(',').map((s) => s.trim());
  const valid = parts.length > 0 && parts.every((p) => p === '0' || p === '1');
  if (!valid) {
    return { states: new Array(count).fill(false), known: new Array(count).fill(false) };
  }
  return {
    states: Array.from({ length: count }, (_, i) => parts[i] === '1'),
    known: Array.from({ length: count }, (_, i) => i < parts.length),
  };
}

export function useRelayStatus(uniqueNumber, count, userId) {
  const [states, setStates] = useState(() => new Array(count).fill(false));
  const [known, setKnown] = useState(() => new Array(count).fill(false));
  const countRef = useRef(count);
  countRef.current = count;

  // Seed from the last status the server recorded for this device.
  useEffect(() => {
    if (!uniqueNumber) return undefined;
    let alive = true;
    fetchSeedStatus(uniqueNumber).then((raw) => {
      if (!alive) return;
      const parsed = parseCsv(raw, countRef.current);
      setStates(parsed.states);
      setKnown(parsed.known);
    });
    return () => { alive = false; };
  }, [uniqueNumber]);

  // Then follow live status.
  useEffect(() => {
    if (!uniqueNumber) return undefined;
    const topic = `${uniqueNumber}/client`;
    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic) return;
      // Guard against other devices' frames arriving on a shared listener.
      const open = payload.indexOf('[');
      const close = payload.indexOf(']');
      if (open === -1 || close === -1) return;
      if (payload.substring(open + 1, close) !== String(uniqueNumber)) return;

      const s = `${payload}#`;
      const body = s.substring(s.indexOf(']') + 1, s.indexOf('#'));
      const bits = body.split(',').map((x) => x.trim());
      setStates((prev) => prev.map((v, i) => (bits[i] === '1' ? true : bits[i] === '0' ? false : v)));
      setKnown((prev) => prev.map((v, i) => (bits[i] === '0' || bits[i] === '1' ? true : v)));
    });
    return () => { off(); unsubscribeTopic(topic); };
  }, [uniqueNumber]);

  // Resize when the relay count is known later (e.g. after outputs load).
  useEffect(() => {
    setStates((prev) => (prev.length === count ? prev
      : Array.from({ length: count }, (_, i) => prev[i] ?? false)));
    setKnown((prev) => (prev.length === count ? prev
      : Array.from({ length: count }, (_, i) => prev[i] ?? false)));
  }, [count]);

  // Send "<relay>-<0|1>" for a 1-based relay index. No optimistic update.
  const toggleRelay = useCallback((index) => {
    const turnOn = !states[index];
    publishCommand(uniqueNumber, `${index + 1}-${turnOn ? '1' : '0'}`, userId);
  }, [states, uniqueNumber, userId]);

  const setAll = useCallback((on) => {
    publishCommand(uniqueNumber, on ? 'allon' : 'alloff', userId);
  }, [uniqueNumber, userId]);

  return {
    states, known, toggleRelay, setAll,
  };
}
